import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Table from "@/components/ui/table";
import { Search as SearchIcon } from "@/components/icons";
import type { TableColumn } from "@/types/component";
import type { Staff } from "@/features/staff-management/types/staff-management.types";
import { staffManagementApi } from "@/features/staff-management/services/staff-management.api";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import { useStore } from "@/store";
import styles from "./AddPersonModal.module.scss";

interface AddPersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  educationId: string | null;
  onAdded?: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

const AddPersonModal: React.FC<AddPersonModalProps> = ({
  isOpen,
  onClose,
  educationId,
  onAdded,
}) => {
  const { t } = useTranslation();
  const currentUser = useStore((s) => s.currentUser);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [users, setUsers] = useState<Staff[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);

  // Debounce search input
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, isOpen]);

  const fetchUsers = useCallback(
    async (page: number, size: number, search?: string) => {
      setIsLoading(true);
      try {
        const { data, total } = await staffManagementApi.getAll({
          page,
          pageSize: size,
          filters: {
            search: search?.trim() || undefined,
          },
        });
        setUsers(data);
        setTotalRecords(total);
      } catch {
        setUsers([]);
        setTotalRecords(0);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // Reset state when modal is opened/closed
  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setDebouncedSearch("");
      setSelectedUserIds([]);
      setCurrentPage(1);
      setPageSize(PAGE_SIZE);
      fetchUsers(1, PAGE_SIZE, "");
    } else {
      setUsers([]);
      setSelectedUserIds([]);
      setTotalRecords(0);
    }
  }, [isOpen, fetchUsers]);

  // Refetch on debounced search
  useEffect(() => {
    if (!isOpen) return;
    setCurrentPage(1);
    fetchUsers(1, pageSize, debouncedSearch);
  }, [debouncedSearch, fetchUsers, isOpen, pageSize]);

  const toggleSelectUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSave = async () => {
    if (!educationId || selectedUserIds.length === 0) return;
    const reviewerUserId = currentUser?.id;
    if (!reviewerUserId) return;

    setIsSubmitting(true);
    try {
      for (const userId of selectedUserIds) {
        // Create approved enrollment for each selected user
        await educationManagementApi.createEnrollment(educationId, {
          userId,
          reviewerUserId,
        });
      }
      setSelectedUserIds([]);
      onAdded?.();
      onClose();
    } catch {
      // Error toast handled by apiClient interceptor
    } finally {
      setIsSubmitting(false);
    }
  };

  const customFooter = (
    <div className={styles.footer}>
      <Button
        variant="text"
        color="secondary"
        className={styles.cancelButton}
        onClick={handleClose}
        disabled={isSubmitting}
      >
        {t("common.cancel")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        className={styles.saveButton}
        onClick={handleSave}
        disabled={
          isSubmitting ||
          !educationId ||
          selectedUserIds.length === 0 ||
          !currentUser?.id
        }
      >
        {isSubmitting
          ? t("common.loading")
          : t("educationOfficer.addPersonModal.saveButton")}
      </Button>
    </div>
  );

  const columns: TableColumn<Staff>[] = [
    {
      header: t("staffManagement.columns.staffId"),
      accessor: "staffId",
      className: "staff-id-column",
    },
    {
      header: t("staffManagement.columns.name"),
      accessor: "name",
      className: "staff-name-column",
    },
    {
      header: t("staffManagement.columns.title"),
      accessor: "title",
      className: "title-column",
    },
    {
      header: t("staffManagement.columns.rootDepartment"),
      accessor: (row) => row.rootDepartment ?? row.department ?? "—",
      className: "department-column",
    },
    {
      header: t("staffManagement.columns.location"),
      accessor: "location",
      className: "location-column",
    },
  ];

  const selectedRows = users.filter((u) => selectedUserIds.includes(u.id));

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchUsers(page, pageSize, debouncedSearch);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchUsers(1, size, debouncedSearch);
  };

  return (
    <Modal
      title={t("educationOfficer.addPersonModal.title")}
      isOpen={isOpen}
      onClose={handleClose}
      size="fullscreen"
      customFooter={customFooter}
    >
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>
          {t("educationOfficer.addPersonModal.searchLabel")}
        </h4>
        <div className={styles.searchRow}>
          <div className={styles.searchInput}>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startIcon={<SearchIcon width={20} height={20} />}
              placeholder={t("educationOfficer.addPersonModal.searchPlaceholder")}
            />
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <Table
          columns={columns}
          data={users}
          isLoading={isLoading}
          selectable
          selectedRows={selectedRows}
          onRowSelect={(row) => toggleSelectUser(row.id)}
          getRowId={(row) => row.id}
          currentPage={currentPage}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          contentBasedHeight
          emptyMessage={t("educationOfficer.addPersonModal.empty")}
        />
      </div>
    </Modal>
  );
};

export default AddPersonModal;

